import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, MagnifyingGlass, CaretLeft, CaretRight, CaretDown, CaretUp,
  Package, User, CalendarBlank, Warning, ClockCountdown, ChartBar,
} from '@phosphor-icons/react';
import { ModuleShell, SkeletonBlock, EmptyState, VolumeBar } from '../../../components/module-ui';
import { apiPath } from '../../../config/apiBase';
import { authHeaders } from '../api/ticketsApi';

const TICKETS_API = apiPath('/api/tickets');
const PAGE_SIZE = 10;

const STATE_COLORS = {
  breached: { bg: '#FEE2E2', fg: '#B91C1C', border: '#FECACA' },
  at_risk: { bg: '#FEF3C7', fg: '#B45309', border: '#FDE68A' },
};

function StateBadge({ state }) {
  const c = STATE_COLORS[state] || { bg: '#F3F4F6', fg: '#374151', border: '#E5E7EB' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
      background: c.bg, color: c.fg, border: `1px solid ${c.border}`, textTransform: 'capitalize',
    }}
    >
      {(state || '').replace(/_/g, ' ')}
    </span>
  );
}

function PipelineTrack({ pins = [] }) {
  return (
    <ol className="sla-pipeline-track" aria-label="Ticket pipeline">
      {pins.map((pin, idx) => (
        <li
          key={`${pin.pin}-${idx}`}
          className={`sla-pipeline-pin${pin.critical ? ' is-critical' : ''}${pin.done ? ' is-done' : ''}`}
        >
          <div className="sla-pipeline-pin-dot" aria-hidden />
          <div className="sla-pipeline-pin-body">
            <div className="sla-pipeline-pin-label">{pin.label}</div>
            <div className="sla-pipeline-pin-what"><strong>What:</strong> {pin.what || '—'}</div>
            <div className="sla-pipeline-pin-who"><strong>Who:</strong> {pin.who || '—'}</div>
            <div className="sla-pipeline-pin-when"><strong>When:</strong> {pin.when_ist || '—'}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * Volume 360° — same trend as Overview, larger + day table + tickets for selected day.
 */
export default function Volume360Page() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const createdOn = searchParams.get('created_on') || '';
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [trend, setTrend] = useState([]);
  const [error, setError] = useState(null);

  const [loadingTickets, setLoadingTickets] = useState(false);
  const [payload, setPayload] = useState(null);
  const [ticketError, setTicketError] = useState(null);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState({});
  const [q, setQ] = useState('');
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${TICKETS_API}/dashboard/trends`, {
      ...authHeaders(),
      params: { days },
    }).then((res) => {
      if (cancelled) return;
      setTrend(res.data?.trend || []);
    }).catch((e) => {
      if (!cancelled) {
        setError(e.response?.data?.detail || 'Could not load volume');
        setTrend([]);
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [days]);

  const chartRows = useMemo(
    () => (trend || []).map((d) => ({
      date: d.date,
      label: (d.date || '').slice(5),
      count: d.count || 0,
      created: d.count || 0,
    })),
    [trend],
  );

  const totals = useMemo(() => {
    const total = chartRows.reduce((n, r) => n + (r.count || 0), 0);
    const peak = chartRows.reduce((m, r) => Math.max(m, r.count || 0), 0);
    const peakDay = chartRows.find((r) => (r.count || 0) === peak)?.date || '';
    const avg = chartRows.length ? Math.round(total / chartRows.length) : 0;
    return { total, peak, peakDay, avg, days: chartRows.length };
  }, [chartRows]);

  const selectDay = (date) => {
    const next = new URLSearchParams(searchParams);
    next.set('mode', 'volume');
    if (date) next.set('created_on', date);
    else next.delete('created_on');
    setSearchParams(next, { replace: true });
    setPage(1);
  };

  const fetchPipeline = useCallback(async () => {
    if (!createdOn) {
      setPayload(null);
      return;
    }
    setLoadingTickets(true);
    setTicketError(null);
    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
        status_group: 'all',
        created_on: createdOn,
      };
      if (q.trim()) params.q = q.trim();
      const res = await axios.get(`${TICKETS_API}/dashboard/ticket-pipeline`, {
        ...authHeaders(),
        params,
      });
      setPayload(res.data);
    } catch {
      setTicketError('Could not load tickets');
      setPayload(null);
    } finally {
      setLoadingTickets(false);
    }
  }, [createdOn, page, q]);

  useEffect(() => { fetchPipeline(); }, [fetchPipeline]);

  useEffect(() => {
    setPage(1);
    setExpanded({});
  }, [createdOn]);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = searchInput.trim();
      if (next === q) return;
      setPage(1);
      setQ(next);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput, q]);

  const items = payload?.items || [];
  const pagination = payload?.pagination || {
    page: 1, page_size: PAGE_SIZE, total: 0, total_pages: 1, has_prev: false, has_next: false,
  };
  const from = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.page_size + 1;
  const to = Math.min(pagination.page * pagination.page_size, pagination.total);

  if (loading && !trend.length) {
    return (
      <ModuleShell title="Volume" subtitle="Loading…">
        <SkeletonBlock rows={10} />
      </ModuleShell>
    );
  }

  return (
    <ModuleShell
      title="Ticket volume"
      subtitle="Same volume chart as Overview, with daily detail. Click a day for tickets created that day."
      headerActions={(
        <button type="button" className="tk-btn tk-btn-secondary" onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={16} />
          Overview
        </button>
      )}
    >
      <div data-testid="volume-360-page" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && (
          <div style={{ padding: 12, background: '#FEE2E2', color: '#B91C1C', borderRadius: 8 }}>{error}</div>
        )}

        <div className="sla-pipeline-summary">
          <div className="sla-summary-chip">
            <span className="sla-summary-chip-value">{totals.days}</span>
            <span className="sla-summary-chip-label">Days</span>
          </div>
          <div className="sla-summary-chip">
            <span className="sla-summary-chip-value">{totals.total}</span>
            <span className="sla-summary-chip-label">Created</span>
          </div>
          <div className="sla-summary-chip">
            <span className="sla-summary-chip-value">{totals.avg}</span>
            <span className="sla-summary-chip-label">Avg / day</span>
          </div>
          <div className="sla-summary-chip">
            <span className="sla-summary-chip-value">{totals.peak}</span>
            <span className="sla-summary-chip-label">Peak day</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#6B7280' }}>Range:</span>
          {[14, 30, 60, 90].map((d) => (
            <button
              key={d}
              type="button"
              className="tk-btn tk-btn-secondary"
              onClick={() => setDays(d)}
              style={{
                borderColor: days === d ? '#002FA7' : undefined,
                background: days === d ? '#EFF6FF' : undefined,
              }}
            >
              {d}d
            </button>
          ))}
          {createdOn && (
            <button type="button" className="tk-btn tk-btn-secondary" onClick={() => selectDay('')}>
              Clear day
            </button>
          )}
        </div>

        <VolumeBar
          data={chartRows}
          title={`Volume (last ${days} days)`}
          height={320}
          onBarClick={(date) => {
            if (!date) return;
            const full = chartRows.find((r) => r.date === date || r.label === date);
            selectDay(full?.date || date);
          }}
        />

        <div className="wd-chart-card wd-soft-card is-auto-height" style={{ margin: 0 }}>
          <p className="wd-chart-title">Daily breakdown</p>
          <p className="wd-chart-subtitle">Click a day to load tickets created that day</p>
          {chartRows.length === 0 ? (
            <EmptyState icon={ChartBar} title="No volume data" description="Nothing in this range." />
          ) : (
            <div style={{ display: 'grid', gap: 6, maxHeight: 360, overflow: 'auto' }}>
              {[...chartRows].reverse().map((row) => {
                const selected = createdOn === row.date;
                const max = Math.max(1, totals.peak);
                const pct = Math.round(((row.count || 0) / max) * 100);
                return (
                  <button
                    key={row.date}
                    type="button"
                    onClick={() => selectDay(row.date)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '110px 1fr 48px',
                      gap: 10,
                      alignItems: 'center',
                      padding: '8px 10px',
                      border: selected ? '2px solid #002FA7' : '1px solid #E5E7EB',
                      borderRadius: 10,
                      background: selected ? '#EFF6FF' : '#fff',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{row.date}</span>
                    <div className="wd-ranked-bar-track" aria-hidden>
                      <div className="wd-ranked-bar-fill" style={{ width: `${Math.max(4, pct)}%` }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#002FA7', textAlign: 'right' }}>
                      {row.count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {createdOn && (
          <section className="sla-pipeline-panel" style={{ margin: 0 }}>
            <div style={{ marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
                Tickets created on {createdOn}
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6B7280' }}>
                All statuses · expand a row for pipeline stages
              </p>
            </div>
            <div className="sla-pipeline-filters">
              <form
                className="sla-api-search"
                onSubmit={(e) => {
                  e.preventDefault();
                  setPage(1);
                  setQ(searchInput.trim());
                }}
              >
                <MagnifyingGlass size={16} className="sla-api-search-icon" aria-hidden />
                <input
                  type="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search tickets…"
                />
                <button type="submit" className="tk-btn tk-btn-secondary">Search</button>
              </form>
            </div>
            {ticketError && <p style={{ color: '#B91C1C', fontSize: 13 }}>{ticketError}</p>}
            {loadingTickets && !payload ? (
              <SkeletonBlock rows={4} />
            ) : items.length === 0 ? (
              <div className="sla-pipeline-empty">
                <ClockCountdown size={28} color="#9CA3AF" />
                <p>No tickets created on this day.</p>
              </div>
            ) : (
              <>
                <div className="sla-pipeline-grid">
                  {items.map((item) => {
                    const open = !!expanded[item.ticket_no];
                    const productName = item.product?.product_name || item.product?.product_key || '—';
                    const assignees = (item.who?.assignee_names || []).join(', ') || 'Unassigned';
                    return (
                      <div key={item.ticket_no} className={`sla-pipeline-card${open ? ' is-expanded' : ''}`}>
                        <div className="sla-pipeline-row-main">
                          <div className="sla-pipeline-row-top">
                            <Link
                              to={`/dashboard/tickets/list/${encodeURIComponent(item.ticket_no)}`}
                              className="sla-ticket-link"
                            >
                              {item.ticket_no}
                            </Link>
                            <StateBadge state={item.primary_state} />
                            {item.what?.status && (
                              <span className="badge">{item.what.status}</span>
                            )}
                          </div>
                          <div className="sla-pipeline-what">
                            <strong>What:</strong> {item.what?.subject || '—'}
                          </div>
                          <div className="sla-pipeline-meta">
                            <span><User size={14} /> {assignees}</span>
                            <span><CalendarBlank size={14} /> {item.when?.created_at_ist || '—'}</span>
                            <span><Package size={14} /> {productName}</span>
                            <span><Warning size={14} /> {item.what?.client || '—'}</span>
                          </div>
                        </div>
                        <div className="sla-pipeline-row-actions">
                          <button
                            type="button"
                            className="tk-btn tk-btn-secondary"
                            onClick={() => setExpanded((prev) => ({ ...prev, [item.ticket_no]: !prev[item.ticket_no] }))}
                          >
                            {open ? <CaretUp size={16} /> : <CaretDown size={16} />}
                            Stages
                          </button>
                        </div>
                        {open && (
                          <div className="sla-pipeline-detail">
                            <PipelineTrack pins={item.pipeline || []} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="sla-pagination">
                  <span className="sla-pagination-meta">Showing {from}–{to} of {pagination.total}</span>
                  <div className="sla-pagination-buttons">
                    <button type="button" className="tk-btn tk-btn-secondary" disabled={!pagination.has_prev || loadingTickets} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                      <CaretLeft size={16} /> Previous
                    </button>
                    <span className="sla-pagination-page">Page {pagination.page} / {pagination.total_pages}</span>
                    <button type="button" className="tk-btn tk-btn-secondary" disabled={!pagination.has_next || loadingTickets} onClick={() => setPage((p) => p + 1)}>
                      Next <CaretRight size={16} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>
        )}
      </div>
    </ModuleShell>
  );
}
